import { useApolloClient } from '@apollo/client';
import { Button, message } from 'antd';
import { Formik } from 'formik';
import { omit, pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../shared/permissions';
import { type WebPageBlockDataFragment } from '../../../api/fragments/WebPageBlockData';
import { type WebPageDataFragment } from '../../../api/fragments/WebPageData';
import {
    UpdateWebPageDocument,
    type UpdateWebPageMutation,
    type UpdateWebPageMutationVariables,
} from '../../../api/mutations/updateWebPage';
import { type CustomWebPageBlockInput, type TextCarouselWebPageBlockInput, WebPageBlockType } from '../../../api/types';
import { useAccount } from '../../../components/contexts/AccountContextManager';
import Form from '../../../components/fields/Form';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import {
    type ColumnBlockMainInput,
    type ImageBlockInput,
    type TextImageBlockInput,
    type WebPageBlockInput,
    type WebpageFormikValue,
} from './shared';
import useHandleWebPageAsset from './useHandleWebPageAsset';
import useWebpageValidator from './useValidator';
import WebpageForm from './WebpageForm';

const getWebpageBlockInput = (webPageBlock: WebPageBlockDataFragment[]): WebPageBlockInput[] =>
    (webPageBlock || []).map(block => {
        switch (block?.__typename) {
            case 'ColumnWebPageBlock':
            case 'CustomWebPageBlock':
            case 'ImageWebPageBlock':
            case 'TextImageWebPageBlock':
                return block;
            case 'TextCarouselWebPageBlock':
                return {
                    ...block,
                    descriptions: block.textDescriptions,
                };

            default:
                throw new Error('webPageBlock not support');
        }
    });

type WebpageDetailsProps = {
    webpage: WebPageDataFragment;
};

const WebpageDetails = ({ webpage }: WebpageDetailsProps) => {
    const { t } = useTranslation(['webpageDetails']);

    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const { permissions: accountPermissions } = useAccount();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: webpage.versioning,
        moduleId: webpage.moduleId,
    });

    const { module } = webpage;
    if (module.__typename !== 'WebsiteModule') {
        throw new Error('Module not support');
    }

    const useWebPageAsset = useHandleWebPageAsset();
    const validate = useWebpageValidator(true);
    const onSubmit = useHandleError<WebpageFormikValue>(
        async value => {
            const { webPageBlock, companyId, moduleId, bannerImage, mobileBannerImage, ...webPageInput } = value;
            // submitting message
            message.loading({
                content: t('webpageDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const { data } = await apolloClient.mutate<UpdateWebPageMutation, UpdateWebPageMutationVariables>({
                mutation: UpdateWebPageDocument,
                variables: {
                    webPageBlockInput: (webPageBlock || [])
                        .map(block => {
                            switch (block.type) {
                                case WebPageBlockType.Column: {
                                    const columnBlockInput = block as ColumnBlockMainInput;

                                    const columnBlock = {
                                        ...pick(['id', 'type', 'columns', 'title'], columnBlockInput),
                                    };

                                    columnBlock.columns = columnBlockInput.columns.map(column => ({
                                        ...omit(['image'], column),
                                    }));

                                    return { columnBlockInput: columnBlock };
                                }

                                case WebPageBlockType.Custom: {
                                    const customBlockInput = block as CustomWebPageBlockInput;

                                    return { customBlockInput };
                                }

                                case WebPageBlockType.Image: {
                                    const imageBlockInput = block as ImageBlockInput;
                                    const imageBlock = {
                                        ...pick(['descriptions', 'type', 'id', 'title', 'button'], imageBlockInput),
                                    };
                                    imageBlock.descriptions = imageBlock.descriptions.map(description => ({
                                        ...omit(['icon'], description),
                                    }));

                                    return { imageBlockInput: imageBlock };
                                }

                                case WebPageBlockType.TextImage: {
                                    const textImageBlockInput = block as TextImageBlockInput;
                                    const typeBlock = { ...omit(['image'], textImageBlockInput) };

                                    return { textImageBlockInput: typeBlock };
                                }

                                case WebPageBlockType.TextCarousel: {
                                    const textCarouselBlockInput = block as TextCarouselWebPageBlockInput;

                                    return {
                                        textCarouselBlockInput: {
                                            ...pick(['id', 'type', 'title', 'descriptions'], textCarouselBlockInput),
                                        },
                                    };
                                }

                                default:
                                    throw new Error('WebPageBlockType not support');
                            }
                        })
                        .filter(Boolean),
                    webPageId: webpage.id,
                    webPageInput,
                },
            });

            await useWebPageAsset(value, data.webpage.id, data.webpage);

            // inform about success
            message.success({
                content: t('webpageDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            navigate(-1);
        },
        [apolloClient, t, useWebPageAsset, webpage.id, navigate]
    );

    const initialValues: WebpageFormikValue = useMemo(
        () => ({
            ...omit(['__typename', 'webPageBlock', 'module', 'versioning', 'id'], webpage),
            webPageBlock: getWebpageBlockInput(webpage?.webPageBlock),
        }),
        [webpage]
    );

    const hasLiveChat = useMemo(
        () => webpage.module.__typename === 'WebsiteModule' && !!webpage.module.liveChatSettingId,
        [webpage.module]
    );

    const hasUpdatePermission = hasPermissions(accountPermissions, [permissionKind.updateWebpage]);

    return (
        <ConsolePageWithHeader
            footer={
                hasUpdatePermission && [
                    <Button key="submit" form="updateWebPageForm" htmlType="submit" type="primary">
                        {t('webpageDetails:actions.update')}
                    </Button>,
                ]
            }
            onBack={() => navigate('/admin/webpages')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate} validateOnChange={false}>
                {({ handleSubmit }) => (
                    <Form id="updateWebPageForm" name="updateWebPageForm" onSubmitCapture={handleSubmit}>
                        <FormAutoTouch />
                        <WebpageForm
                            hasLiveChat={hasLiveChat}
                            moduleDisplayName={module.displayName}
                            offset={offset}
                            permissions={{ hasUpdatePermission }}
                            updated={updated}
                        />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default WebpageDetails;
